/**
 * Pure formatters shared by the HUMINT components. Pass `i18n.language` as `locale` so a
 * Slovak screen reads "0,64" and "29. 9. 2026" while an English one reads "0.64". Every
 * `Intl` call goes through `intlLocale()` (Thai stays on Gregorian years) and a formatter
 * cache, so a list of a hundred rows builds each formatter once.
 */
import type { TFunction } from 'i18next'
import { intlLocale } from '@/services/i18n/languages'
import type { CheckCandidate, Sex, SubjectSummary } from './types'

type Formatter = Intl.NumberFormat | Intl.DateTimeFormat

const formatters = new Map<string, Formatter>()

/** One formatter per language + options, built on first use. */
function cached<T extends Formatter>(key: string, make: () => T): T {
  let formatter = formatters.get(key) as T | undefined
  if (!formatter) {
    formatter = make()
    formatters.set(key, formatter)
  }
  return formatter
}

/** A cached `Intl.NumberFormat` for a UI language and options. */
export function numberFormat(locale: string, options: Intl.NumberFormatOptions): Intl.NumberFormat {
  const tag = intlLocale(locale)
  return cached(`n|${tag}|${JSON.stringify(options)}`, () => new Intl.NumberFormat(tag, options))
}

/** A cached `Intl.DateTimeFormat` for a UI language and options. */
export function dateTimeFormat(
  locale: string,
  options: Intl.DateTimeFormatOptions
): Intl.DateTimeFormat {
  const tag = intlLocale(locale)
  return cached(`d|${tag}|${JSON.stringify(options)}`, () => new Intl.DateTimeFormat(tag, options))
}

/** Similarity or ratio with two decimals: 0.64 → "0,64" (sk). */
export function formatSimilarity(value: number, locale: string): string {
  return numberFormat(locale, { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(value)
}

/** Whole percent: 0.62 → "62 %" (sk). */
export function formatPercent(value: number, locale: string): string {
  return numberFormat(locale, { style: 'percent', maximumFractionDigits: 0 }).format(value)
}

/** Clock time of an ISO timestamp: "14:05". */
export function formatClock(iso: string, locale: string): string {
  return dateTimeFormat(locale, { hour: '2-digit', minute: '2-digit' }).format(new Date(iso))
}

/** Numeric date of an ISO timestamp: "29. 9. 2026" (sk). */
export function formatShortDate(iso: string, locale: string): string {
  return dateTimeFormat(locale, { day: 'numeric', month: 'numeric', year: 'numeric' }).format(
    new Date(iso)
  )
}

/** Numeric date and clock time of an ISO timestamp: "29. 9. 2026 · 14:05" (sk). */
export function formatDateClock(iso: string, locale: string): string {
  return `${formatShortDate(iso, locale)} · ${formatClock(iso, locale)}`
}

/** Day and month of an ISO timestamp: "27. 9." (sk), "9/27" (en). */
export function formatDayMonth(iso: string, locale: string): string {
  return dateTimeFormat(locale, { day: 'numeric', month: 'numeric' }).format(new Date(iso))
}

/** Weekday, day and month of an ISO timestamp: "štvrtok 1. 10." (sk); the year with `withYear`. */
export function formatWeekdayDate(iso: string, locale: string, withYear = false): string {
  const year = withYear ? ({ year: 'numeric' } as const) : {}
  return dateTimeFormat(locale, {
    weekday: 'long',
    day: 'numeric',
    month: 'numeric',
    ...year,
  }).format(new Date(iso))
}

/** The side of a grid square: 1000 → "1 km", 100 → "100 m". */
export function formatGridSize(meters: number, locale: string): string {
  const km = meters >= 1000
  const unit = km ? 'kilometer' : 'meter'
  return numberFormat(locale, { style: 'unit', unit, unitDisplay: 'short' }).format(
    km ? meters / 1000 : meters
  )
}

/** Transcript offset or duration: 192000 → "03:12", 4000000 → "1:06:40". */
export function formatOffset(ms: number): string {
  const total = Math.max(0, Math.floor(ms / 1000))
  const h = Math.floor(total / 3600)
  const m = Math.floor((total % 3600) / 60)
  const s = total % 60
  const pad = (n: number) => String(n).padStart(2, '0')
  return h > 0 ? `${h}:${pad(m)}:${pad(s)}` : `${pad(m)}:${pad(s)}`
}

/** Latitude / longitude in decimal degrees with 4 places: "49,0612° · 20,2984°" (sk). */
export function formatLatLon(lat: number, lon: number, locale: string): string {
  const f = numberFormat(locale, { minimumFractionDigits: 4, maximumFractionDigits: 4 })
  return `${f.format(lat)}° · ${f.format(lon)}°`
}

/** The tag a subject is known by: the capture tag of a detention, else the subject number. */
export function subjectTag(subject: Pick<SubjectSummary, 'captureTag' | 'subjectNo'>): string {
  return subject.captureTag ?? subject.subjectNo
}

/**
 * The tag of a workspace subject without a capture tag: the clock time of its enrolment and
 * its short id, "14:05 · 7f3a" (C4); just the id when the enrolment time is not known. Beside
 * a translated *Unknown* it reads "Neznámy · 14:05 · 7f3a".
 */
export function timeTag(at: string | null | undefined, shortId: string, locale: string): string {
  return at ? `${formatClock(at, locale)} · ${shortId}` : shortId
}

/** The tag a workspace subject is known by: the capture tag of a detention, else time and short id. */
export function workspaceTag(
  subject: { captureTag?: string | null; subjectNo: string; enrolledAt?: string | null },
  locale: string
): string {
  return subject.captureTag ?? timeTag(subject.enrolledAt, subject.subjectNo, locale)
}

/**
 * The i18n `context` of a subject's grammatical gender ("Neznámy / Neznáma", "Nie je to on /
 * ona"): `male` / `female`, or undefined when unknown so the neutral key is used.
 */
export function sexContext(sex: Sex | null | undefined): 'male' | 'female' | undefined {
  return sex === 'male' || sex === 'female' ? sex : undefined
}

/** Given name and surname as one string; empty when neither is known. */
export function joinedName(subject: Pick<SubjectSummary, 'givenName' | 'surname'>): string {
  return [subject.givenName, subject.surname].filter(Boolean).join(' ')
}

/** A name to show, or the translated "Unknown" (gendered in cs/sk when `sex` is known). */
export function nameOrUnknown(
  name: string | null | undefined,
  t: TFunction,
  sex?: Sex | null
): string {
  return name || t('humint.common.unknown', { context: sexContext(sex) })
}

/**
 * The subject's name, or the translated "Unknown" (gendered in cs/sk via i18n context).
 * The tag is shown next to it in mono, never folded into this string.
 */
export function subjectName(
  subject: Pick<SubjectSummary, 'givenName' | 'surname' | 'sex'>,
  t: TFunction
): string {
  return nameOrUnknown(joinedName(subject), t, subject.sex)
}

/**
 * A face-check candidate's name: the subject's name, or the one wording of an unassigned
 * face, "Unknown face – seen 27. 9." (or "… seen before" without a date).
 */
export function candidateName(
  candidate: Pick<CheckCandidate, 'kind' | 'subject' | 'source'>,
  t: TFunction,
  locale: string
): string {
  if (candidate.kind === 'subject' && candidate.subject) return subjectName(candidate.subject, t)
  const seen = candidate.source?.at
  return seen
    ? t('humint.common.unassignedFace', { date: formatDayMonth(seen, locale) })
    : t('humint.common.unassignedFaceUndated')
}

/**
 * What is said of a photo added from a file instead of taken in the workspace (ADR-0059): that
 * it is imported, and when the photo itself says it was taken — or that it does not say.
 */
export function importedText(
  photo: { takenAt?: string | null },
  t: TFunction,
  locale: string
): string {
  return photo.takenAt
    ? t('humint.common.imported.dated', { time: formatDateClock(photo.takenAt, locale) })
    : t('humint.common.imported.undated')
}
